import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Award, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { homeContentAPI, getUploadUrl } from '../services/api';

const iconMap = {
  Award: Award,
  Users: Users,
  TrendingUp: TrendingUp,
  Sparkles: Sparkles,
};

const defaultHeroImage = 'https://images.unsplash.com/photo-1606206873764-fd15e242df52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Njk2ODA5OTl8MA&ixlib=rb-4.1.0&q=85';

export const Home = () => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState(null);
  const lang = i18n.language === 'en' ? 'en' : 'it';

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await homeContentAPI.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error loading home content:', error);
    }
  };

  // Use dynamic content or fallback to defaults
  const heroBadge = content ? (lang === 'en' ? content.hero_badge_en : content.hero_badge_it) : t('hero.badge');
  const heroImage = content?.hero_image ? getUploadUrl(content.hero_image) : defaultHeroImage;
  
  const stats = content?.stats?.map(stat => ({
    icon: iconMap[stat.icon] || Award,
    value: stat.value,
    label: lang === 'en' ? stat.label_en : stat.label_it
  })) || [
    { icon: Award, value: '25+', label: lang === 'en' ? 'Years Experience' : 'Anni di Esperienza' },
    { icon: Users, value: '500+', label: lang === 'en' ? 'Happy Clients' : 'Clienti Soddisfatti' },
    { icon: TrendingUp, value: '98%', label: lang === 'en' ? 'Satisfaction Rate' : 'Tasso Soddisfazione' },
    { icon: Sparkles, value: '1000+', label: lang === 'en' ? 'Products' : 'Prodotti' },
  ];

  const features = content?.features?.map(feature => ({
    title: lang === 'en' ? feature.title_en : feature.title_it,
    description: lang === 'en' ? feature.description_en : feature.description_it
  })) || [
    {
      title: lang === 'en' ? 'Certified Quality' : 'Qualità Certificata',
      description: lang === 'en' ? 'All our products meet the most rigorous international standards' : 'Tutti i nostri prodotti rispettano gli standard internazionali più rigorosi',
    },
    {
      title: lang === 'en' ? 'Continuous Innovation' : 'Innovazione Continua',
      description: lang === 'en' ? 'We constantly invest in R&D to offer cutting-edge solutions' : 'Investiamo costantemente in ricerca e sviluppo per offrire soluzioni all\'avanguardia',
    },
    {
      title: lang === 'en' ? 'Dedicated Support' : 'Supporto Dedicato',
      description: lang === 'en' ? 'Our team is always available to assist customers' : 'Il nostro team è sempre disponibile per assistere i clienti',
    },
    {
      title: lang === 'en' ? 'Fast Delivery' : 'Consegna Rapida',
      description: lang === 'en' ? 'Efficient logistics to ensure optimal delivery times' : 'Logistica efficiente per garantire tempi di consegna ottimali',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-dark">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>

        {/* Hero Image Overlay */}
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        ></div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full glass mb-6">
                <Sparkles className="w-4 h-4 text-primary mr-2" />
                <span className="text-sm font-medium text-white">{heroBadge}</span>
              </div>
              
              <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl mb-6 text-white leading-tight">
                {t('hero.title')}
                <span className="block gradient-text mt-2">{t('hero.subtitle')}</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t('hero.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary-dark text-white shadow-elegant hover:shadow-glow transition-all duration-300 group"
                >
                  <Link to="/products">
                    {t('hero.cta')}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="glass border-white/20 text-white hover:bg-white/10"
                >
                  <Link to="/contact">
                    {t('hero.ctaSecondary')}
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            ></motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card text-center">
                  <CardContent className="pt-6">
                    <stat.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                    <div className="text-3xl font-display font-bold text-foreground mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-4xl lg:text-5xl mb-6 text-foreground">
                {t('about.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {t('about.description')}
              </p>
              <div className="space-y-4 mb-8">
                {[
                  lang === 'en' ? 'Innovation' : 'Innovazione', 
                  lang === 'en' ? 'Quality' : 'Qualità', 
                  lang === 'en' ? 'Reliability' : 'Affidabilità'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-foreground font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" variant="outline">
                <Link to="/about">
                  {lang === 'en' ? 'Learn More About Us' : 'Scopri di più'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden glass-card">
                <img
                  src="https://images.unsplash.com/photo-1578496480240-32d3e0c04525?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Njk2ODA5OTl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Tecnologia medica: apparecchiature diagnostiche in un reparto ospedaliero"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary rounded-full blur-3xl opacity-50"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-4xl lg:text-5xl mb-4 text-foreground">
              {lang === 'en' ? 'Why Choose Gmed' : 'Perché Scegliere Gmed'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {lang === 'en' ? 'We deliver excellence in every aspect of our service' : 'Offriamo eccellenza in ogni aspetto del nostro servizio'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Check className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-display font-semibold text-xl mb-2 text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display font-bold text-4xl lg:text-5xl mb-6 text-white">
              {lang === 'en' ? 'Ready to Innovate?' : 'Pronti a Innovare?'}
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              {lang === 'en' 
                ? 'Contact us today to discover how we can support your facility with cutting-edge solutions.' 
                : 'Contattaci oggi per scoprire come possiamo supportare la tua struttura con soluzioni all\'avanguardia.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100"
              >
                <Link to="/contact">
                  {t('hero.ctaSecondary')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Link to="/products">
                  {lang === 'en' ? 'View Products' : 'Vedi Prodotti'}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
